import { ErrorHandler, Injectable } from '@angular/core';
import { logger } from './telemetry/logger';

/**
 * Recovers from stale lazy-chunk references after a new deployment.
 *
 * When a deploy replaces hashed chunk files, a browser still running the
 * previous build requests chunk URLs that no longer exist. On the S3 +
 * CloudFront SPA setup these 404s are rewritten to index.html, so the dynamic
 * import fails with a MIME / "Failed to fetch dynamically imported module"
 * error. Reloading once pulls the fresh index.html and the current chunks.
 */
const RELOAD_FLAG = 'chunk-reload-attempted';

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Expected a JavaScript-or-Wasm module script'
];

@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    const message = this.extractMessage(error);

    if (message && CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))) {
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        return;
      }
    } else {
      sessionStorage.removeItem(RELOAD_FLAG);
    }

    logger.error('Unhandled error', error);
  }

  private extractMessage(error: unknown): string {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error instanceof Error) return `${error.message} ${error.stack ?? ''}`;
    const maybe = error as { message?: unknown; toString?: () => string };
    if (typeof maybe.message === 'string') return maybe.message;
    return maybe.toString ? maybe.toString() : '';
  }
}
